import React from 'react';
import { Globe, BadgeCheck, Clock, Sparkles, Waves, TreePalm, Plane, Bug, Calculator } from 'lucide-react';
import { heroImages } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';

const FeaturesSection = () => {
  const { t } = useLanguage();

  const features = [
    {
      icon: Globe,
      title: t('features.items.multilingual'),
      description: t('features.items.multilingualDesc')
    },
    {
      icon: BadgeCheck,
      title: t('features.items.noGringo'),
      description: t('features.items.noGringoDesc')
    },
    {
      icon: Clock,
      title: t('features.items.experience'),
      description: t('features.items.experienceDesc')
    },
    {
      icon: Sparkles,
      title: t('features.items.sparkly'),
      description: t('features.items.sparklyDesc')
    }
  ];

  const aLaCarteServices = [
    { name: t('features.services.deepCleaning'), icon: Sparkles },
    { name: t('features.services.airportTransfers'), icon: Plane },
    { name: t('features.services.poolMaintenance'), icon: Waves },
    { name: t('features.services.lawnGarden'), icon: TreePalm },
    { name: t('features.services.pestControl'), icon: Bug },
    { name: t('features.services.expensesTracking'), icon: Calculator }
  ];

  return (
    <section id="services" className="py-24 bg-white relative overflow-hidden" aria-label="Property Management Services">
      {/* Decorative background */}
      <div className="absolute top-0 right-0 w-96 h-96 opacity-10">
        <img
          src={heroImages.hibiscus}
          alt="Tropical hibiscus flower decoration"
          className="w-full h-full object-cover"
        />
      </div>

      <div className="max-w-7xl mx-auto px-6 relative">
        {/* Section header */}
        <div className="text-center mb-16">
          <span className="inline-block text-[#000080] bg-[#FFFF00] font-semibold tracking-wider uppercase text-sm mb-4 px-4 py-1 rounded-full">
            {t('features.badge')}
          </span>
          <h2 className="font-display text-4xl md:text-5xl font-bold text-[#000080] mb-6">
            {t('features.title1')}{' '}
            <span className="text-tropical-gradient">{t('features.title2')}</span>
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            {t('features.subtitle')}
          </p>
        </div>

        {/* Features grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
          {features.map((feature, index) => {
            const IconComponent = feature.icon;
            const colors = [
              'from-[#FF6B35]/30 to-[#FF6B35]/10', // Orange
              'from-[#22C55E]/30 to-[#22C55E]/10', // Green
              'from-[#8B5CF6]/30 to-[#8B5CF6]/10', // Purple
              'from-[#00BFFF]/30 to-[#00BFFF]/10'  // Blue
            ];
            const iconColors = ['text-[#FF6B35]', 'text-[#22C55E]', 'text-[#8B5CF6]', 'text-[#00BFFF]'];
            return (
              <div
                key={`feature-${feature.title}-${index}`}
                className="card-tropical bg-white rounded-2xl p-8 shadow-lg border border-gray-100 text-center"
              >
                <div className={`feature-icon w-16 h-16 mx-auto mb-6 rounded-full bg-gradient-to-br ${colors[index % 4]} flex items-center justify-center`}>
                  <IconComponent className={`w-8 h-8 ${iconColors[index % 4]}`} />
                </div>
                <h3 className="text-xl font-bold text-[#000080] mb-3">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            );
          })}
        </div>

        {/* À La Carte Services */}
        <div className="bg-gradient-to-br from-[#000080] to-[#000050] rounded-3xl p-10 md:p-14">
          <div className="text-center mb-10">
            <h3 className="font-display text-3xl font-bold text-white mb-4">
              {t('features.alaCarte')}
            </h3>
            <p className="text-white/70">{t('features.alaCarteSubtitle')}</p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {aLaCarteServices.map((service, index) => {
              const IconComponent = service.icon;
              const iconColors = ['text-[#FF6B35]', 'text-[#22C55E]', 'text-[#8B5CF6]', 'text-[#FFFF00]', 'text-[#00BFFF]', 'text-[#FF6B35]'];
              const bgColors = ['bg-[#FF6B35]/30', 'bg-[#22C55E]/30', 'bg-[#8B5CF6]/30', 'bg-[#FFFF00]/30', 'bg-[#00BFFF]/30', 'bg-[#FF6B35]/30'];
              return (
                <div
                  key={`service-${service.name}-${index}`}
                  className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center hover:bg-white/20 transition-colors cursor-pointer group"
                >
                  <div className={`w-12 h-12 mx-auto mb-3 rounded-full ${bgColors[index % 6]} flex items-center justify-center group-hover:scale-110 transition-transform`}>
                    <IconComponent className={`w-6 h-6 ${iconColors[index % 6]}`} />
                  </div>
                  <p className="text-white text-sm font-medium">{service.name}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;
